import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { useZoneFor } from "@/lib/facility/zone";
import { CallHistory } from "@/routes/_authenticated/_front-desk/sarah/-components/call-history";
import { CallSmsCard } from "@/routes/_authenticated/_front-desk/sarah/-components/call-sms-card";
import { textActionFor } from "@/routes/_authenticated/_front-desk/sarah/-components/call-text-action";
import { CallDetailView } from "@/routes/_authenticated/_front-desk/sarah/-components/call-detail-view";
/* The guard from its own module: validateSearch is not split out with the
   component, and through call-detail-view it dragged the telephony SDK into
   the router's entry chunk on every page. */
import {
  isDetailTab,
  type DetailTab,
} from "@/routes/_authenticated/_front-desk/sarah/-components/call-detail-tabs";
import { useHandledCheck } from "@/routes/_authenticated/_front-desk/sarah/-components/use-handled-check";
import { FollowUpCard } from "@/routes/_authenticated/_front-desk/sarah/-components/follow-up-card";
import { sarah, errorWords } from "@alm/domain";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/calls/$callId")({
  /** `?tab=messages` is what the log's Text action asks for; any tab of the detail page may be
   * named, and anything else is dropped. Optional in the returned type on purpose: every other link
   * to a call would otherwise have to carry a search object it has no opinion about. */
  validateSearch: (search: Record<string, unknown>): { tab?: DetailTab } =>
    isDetailTab(search.tab) ? { tab: search.tab } : {},
  component: CallDetail,
});

const backToLog = {
  tab: undefined,
  q: undefined,
  assignee: undefined,
  residentId: undefined,
  transferredTo: undefined,
  days: undefined,
  from: undefined,
  to: undefined,
  offset: undefined,
};

/** Copy the caller's number, and say whether it happened. */
const copyNumber = (value: string) => {
  void navigator.clipboard
    .writeText(value)
    .then(() => toast.success(`Copied ${value}`))
    .catch(() => toast.error("Could not copy the number. Select it and copy it by hand."));
};

/** One call. No local state of its own: everything is derived from the one query, and the view
 * keeps only the chosen tab. Keyed by the call so a new call is a new page and the tab does not
 * carry over. */
function CallDetail() {
  const zoneFor = useZoneFor();
  const { callId } = Route.useParams();
  const { tab } = Route.useSearch();
  const canTriage = useCan("frontdesk:call");
  const queryClient = useQueryClient();

  const call = useQuery(orpc.frontDesk.calls.get.queryOptions({ input: { callId } }));

  const triage = useMutation(
    orpc.frontDesk.calls.triage.mutationOptions({
      onSuccess: async (_result, input) => {
        toast.success(input.done ? "Marked handled" : "Reopened");
        /* The call and the lists it sits on, not the whole application. */
        await queryClient.invalidateQueries({
          queryKey: orpc.frontDesk.calls.key(),
        });
      },
      onError: (error: Error, input) => {
        if (!check.recover(error, input.callId)) toast.error(errorWords(error));
      },
    }),
  );
  const check = useHandledCheck(triage.mutate);

  if (call.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (call.isError) {
    return (
      <>
        {/* The page's one heading, which the empty state below does not carry. */}
        <h1 className="sr-only">Call</h1>
        <EmptyState
          title="That call is not available"
          description={errorWords(
            call.error,
            "It may have been removed, or it belongs to another building.",
          )}
          action={
            <Button asChild>
              <Link to="/sarah/calls" search={backToLog}>
                Back to the call log
              </Link>
            </Button>
          }
        />
      </>
    );
  }

  const detail = call.data;
  const timeZone = zoneFor(detail.facilityId);
  /* The other party, whichever way the call went: a call placed from the desk
     has nobody in its source, and the person is in `toNumber`. */
  const number =
    detail.direction === "outbound"
      ? sarah.callerNumberFrom(`phone:${detail.toNumber ?? ""}`)
      : sarah.callerNumberFrom(detail.source);

  return (
    <>
      {check.dialog}
      <CallDetailView
        key={callId}
        call={detail}
        timeZone={timeZone}
        canTriage={canTriage && detail.triage !== "none"}
        triagePending={triage.isPending}
        onTriage={(done) => (done ? check.tick(detail) : triage.mutate({ callId, done }))}
        onCopyNumber={copyNumber}
        initialTab={tab}
        followUp={
          <FollowUpCard
            facilityId={detail.facilityId}
            entityType="call"
            entityId={callId}
            heading="h2"
            className="bg-sunken border-border-soft rounded-lg py-3 shadow-none [&>[data-slot=card-content]]:px-4"
          />
        }
        sms={
          textActionFor(detail) !== null ? (
            <CallSmsCard
              callId={callId}
              facilityId={detail.facilityId}
              source={detail.source}
              flags={detail.flags}
            />
          ) : undefined
        }
        history={number ? <CallHistory callId={callId} timeZone={timeZone} /> : undefined}
      />
    </>
  );
}
